import { useCallback, useRef, useEffect } from 'react';
import { designApi, type GenerateDesignRequest, type DesignTask } from '../services/api';
import { useDesignStore } from '../stores/designStore';

const POLL_INTERVAL = 2000; // 每2秒轮询一次
const MAX_POLL_TIME = 10 * 60 * 1000; // 最大轮询10分钟

export interface UseAsyncDesignOptions {
  onSuccess?: (task: DesignTask) => void;
  onError?: (error: string) => void;
  onProgress?: (task: DesignTask) => void;
}

export function useAsyncDesign(options: UseAsyncDesignOptions = {}) {
  const {
    currentTask,
    isGenerating,
    isPolling,
    setCurrentTask,
    setGenerating,
    setIsPolling,
    updateTaskStatus,
    clearTask,
  } = useDesignStore();

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 清理轮询
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsPolling(false);
  }, [setIsPolling]);

  // 轮询任务状态
  const pollTaskStatus = useCallback(async (taskId: string) => {
    try {
      const task = await designApi.getTaskStatus(taskId);
      updateTaskStatus(task);
      options.onProgress?.(task);

      // 任务完成或失败，停止轮询
      if (task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled') {
        stopPolling();
        
        if (task.status === 'completed') {
          options.onSuccess?.(task);
        } else if (task.status === 'failed') {
          options.onError?.(task.error || '生成失败');
        }
        return;
      }

      // 检查是否超过最大轮询时间
      if (Date.now() - startTimeRef.current > MAX_POLL_TIME) {
        stopPolling();
        options.onError?.('生成超时，请稍后查看任务状态');
        return;
      }
    } catch (error) {
      console.error('Poll task status error:', error);
      // 轮询出错，继续尝试
    }
  }, [updateTaskStatus, stopPolling, options]);

  // 开始轮询
  const startPolling = useCallback((taskId: string) => {
    startTimeRef.current = Date.now();
    setIsPolling(true);
    
    // 立即查询一次
    pollTaskStatus(taskId);
    
    // 设置定时轮询
    pollTimerRef.current = setInterval(() => {
      pollTaskStatus(taskId);
    }, POLL_INTERVAL);
  }, [setIsPolling, pollTaskStatus]);

  // 提交设计任务
  const submitDesignTask = useCallback(async (request: GenerateDesignRequest) => {
    // 清理之前的任务
    clearTask();
    stopPolling();
    
    setGenerating(true);
    abortControllerRef.current = new AbortController();

    try {
      // 创建任务
      const task = await designApi.createTask(request);
      setCurrentTask(task);
      
      // 开始轮询
      startPolling(task.id);
      
      return task;
    } catch (error: any) {
      setGenerating(false);
      const errorMessage = error.response?.data?.message || error.message || '创建任务失败';
      options.onError?.(errorMessage);
      throw error;
    }
  }, [clearTask, stopPolling, setGenerating, setCurrentTask, startPolling, options]);

  // 取消当前任务
  const cancelTask = useCallback(async () => {
    if (!currentTask) return;

    stopPolling();
    
    try {
      await designApi.cancelTask(currentTask.id);
      clearTask();
    } catch (error: any) {
      console.error('Cancel task error:', error);
      // 即使取消失败，也清理本地状态
      clearTask();
    }
  }, [currentTask, stopPolling, clearTask]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // 获取任务状态文本
  const getStatusText = useCallback((status: string) => {
    const statusMap: Record<string, string> = {
      pending: '等待中...',
      processing: '生成中...',
      completed: '已完成',
      failed: '生成失败',
      cancelled: '已取消',
    };
    return statusMap[status] || status;
  }, []);

  // 获取预估剩余时间
  const getEstimatedTime = useCallback(() => {
    if (!currentTask?.startedAt) return null;
    
    const startTime = new Date(currentTask.startedAt).getTime();
    const elapsed = Date.now() - startTime;
    
    // 根据已用时间估算剩余时间（假设平均需要60秒）
    const estimatedTotal = 60000;
    const remaining = Math.max(0, estimatedTotal - elapsed);
    
    if (remaining < 10000) return '即将完成...';
    return `预计还需 ${Math.ceil(remaining / 1000)} 秒`;
  }, [currentTask]);

  return {
    // 状态
    currentTask,
    isGenerating,
    isPolling,
    
    // 方法
    submitDesignTask,
    cancelTask,
    stopPolling,
    
    // 辅助
    getStatusText,
    getEstimatedTime,
  };
}

export default useAsyncDesign;
